import { useState, useEffect, ReactNode } from "react";

interface FAQItem {
	question: string;
	answer: string;
	answerNode?: ReactNode;
}

interface FAQSectionProps {
	heading?: string;
	items?: FAQItem[];
}

export default function FAQSection({ heading = "Frequently Asked Questions", items = [] }: FAQSectionProps) {
	const [openIndex, setOpenIndex] = useState<number | null>(0);

	useEffect(() => {
		let injected = false;

		if (items?.length) {
			const ssrFaqExists = Array.from(document.querySelectorAll('script[type="application/ld+json"]')).some((el) => {
				if (el.id === "faq-jsonld") return false;
				try {
					const d = JSON.parse(el.textContent || "");
					const arr = Array.isArray(d) ? d : [d];
					return arr.some((s: any) => s?.["@type"] === "FAQPage");
				} catch {
					return false;
				}
			});

			if (!ssrFaqExists) {
				const script = document.createElement("script");
				script.type = "application/ld+json";
				script.id = "faq-jsonld";
				script.innerHTML = JSON.stringify({
					"@context": "https://schema.org",
					"@type": "FAQPage",
					name: heading,
					mainEntity: items.map(({ question, answer }) => ({
						"@type": "Question",
						name: question,
						acceptedAnswer: { "@type": "Answer", text: answer },
					})),
				});
				document.head.appendChild(script);
				injected = true;
			}
		}

		return () => {
			if (injected) document.getElementById("faq-jsonld")?.remove();
		};
	}, [items]);

	if (!items || items.length === 0) return null;

	const toggle = (i: number) => setOpenIndex(openIndex === i ? null : i);

	return (
		<div className="faq-section">
			<div className="faq-section__header">
				<span className="faq-section__pill">FAQ</span>
				<h2 className="faq-section__heading">{heading}</h2>
			</div>
			<div className="faq-section__list">
				{items.map(({ question, answer, answerNode }, i) => {
					const isOpen = openIndex === i;
					return (
						<div key={i} className={`faq-item${isOpen ? " faq-item--open" : ""}`}>
							<button className="faq-item__trigger" onClick={() => toggle(i)} aria-expanded={isOpen}>
								<span className="faq-item__question">{question}</span>
								<span className="faq-item__toggle" aria-hidden="true">
									{isOpen ? "−" : "+"}
								</span>
							</button>
							{isOpen && (
								<div className="faq-item__answer">
									{answerNode ?? <p>{answer}</p>}
								</div>
							)}
						</div>
					);
				})}
			</div>
		</div>
	);
}
